import React, { useState, useEffect } from "react"
import {
  PencilIcon,
  PlusCircleIcon,
  TrashIcon,
  XMarkIcon,
  CheckIcon
} from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import DataTable from "aldoo-ra/DataTable"
import Typography from "aldoo-ra/Typography"
import Checkbox from "aldoo-ra/Checkbox"
import DropDownSelect from "aldoo-ra/DropdownSelect"
import ProgressBar from "aldoo-ra/ProgressBar"
import ToggleSwitch from "aldoo-ra/ToggleSwitch" // Added import for ToggleSwitch
import { showNotification } from "aldoo-ra/Notification"
import { showPromptDialog } from "aldoo-ra/PromptDialog"
import { GetSpamRules, UpdateSpamRule, DeleteSpamRule } from "./api"
import AddRuleDialog from "./AddRuleDialog"
import { twMerge } from "tailwind-merge"

export default function SpamRuleBrowser() {
  const [refreshKey, setRefreshKey] = useState(0)
  const [selectedItems, setSelectedItems] = useState([])
  const [addRuleDialog, setAddRuleDialog] = useState({
    isOpen: false,
    rule: null,
  })
  const [processing, setProcessing] = useState(false)
  const [activeFilter, setActiveFilter] = useState([
    { value: null, label: "All Rules" },
  ])
  const [targetFilter, setTargetFilter] = useState([
    { value: null, label: "All Targets" },
  ])
  const [scoreFilter, setScoreFilter] = useState([
    { value: null, label: "All Scores" },
  ])

  // Helper function to get color class based on score
  const getScoreColor = (scoreValue) => {
    if (scoreValue >= 0.7) return "text-admin_error"
    if (scoreValue >= 0.4) return "text-admin_warning"
    return "text-admin_success"
  }

  // Add this useEffect to trigger refresh when filters change
  useEffect(() => {
    setRefreshKey((prev) => prev + 1)
  }, [activeFilter, targetFilter, scoreFilter])

  // Handle select all change
  const handleSelectAll = (items, isSelected) => {
    if (isSelected) {
      setSelectedItems(items.map((item) => item._id))
    } else {
      setSelectedItems([])
    }
  }

  // Handle individual selection change
  const handleSelectItem = (itemId, isSelected) => {
    if (isSelected) {
      setSelectedItems((prev) => [...prev, itemId])
    } else {
      setSelectedItems((prev) => prev.filter((id) => id !== itemId))
    }
  }

  // Handle clearing selection
  const handleClearSelection = () => {
    setSelectedItems([])
  }

  // Handle edit rule
  const handleEditRule = (rule) => {
    setAddRuleDialog({
      isOpen: true,
      rule: rule,
    })
  }

  // Handle toggling rule status
  const handleToggleRuleStatus = async (rule, newStatus) => {
    try {
      const response = await UpdateSpamRule(rule._id, {
        active: newStatus,
      })

      if (response.success) {
        showNotification({
          message: `Rule ${
            newStatus ? "activated" : "deactivated"
          } successfully`,
          className: "bg-admin_primary text-admin_text_inv",
        })
        setRefreshKey((prev) => prev + 1)
      } else {
        showNotification({
          message:
            response.error ||
            `Failed to ${newStatus ? "activate" : "deactivate"} rule`,
          className: "bg-admin_error text-white",
        })
      }
    } catch (error) {
      showNotification({
        message: `An error occurred while updating rule status`,
        className: "bg-admin_error text-white",
      })
    }
  }

  // Handle deleting a rule
  const handleDeleteRule = (rule) => {
    showPromptDialog({
      variant: "admin",
      title: "Confirm Delete",
      message: `Are you sure you want to delete this rule? This action cannot be undone.`,
      confirmText: "Delete",
      confirmVariant: "error_m",
      onConfirm: async () => {
        try {
          const response = await DeleteSpamRule(rule._id)

          if (response.success) {
            showNotification({
              message: "Rule deleted successfully",
              className: "bg-admin_primary text-admin_text_inv",
            })
            setRefreshKey((prev) => prev + 1)
          } else {
            showNotification({
              message: response.error || "Failed to delete rule",
              className: "bg-admin_error text-white",
            })
          }
        } catch (error) {
          showNotification({
            message: "An error occurred while deleting rule",
            className: "bg-admin_error text-white",
          })
        }
      },
    })
  }

  // Handle activating multiple rules
  const handleActivateSelected = () => {
    if (selectedItems.length === 0 || processing) return

    showPromptDialog({
      variant: "admin",
      title: "Confirm Activate",
      message: `Are you sure you want to activate ${selectedItems.length} ${
        selectedItems.length === 1 ? "rule" : "rules"
      }?`,
      confirmText: "Activate",
      confirmVariant: "primary",
      onConfirm: async () => {
        setProcessing(true)
        try {
          let successCount = 0
          let errorCount = 0

          // Process each rule sequentially
          for (const ruleId of selectedItems) {
            try {
              const response = await UpdateSpamRule(ruleId, {
                active: true,
              })

              if (response.success) {
                successCount++
              } else {
                errorCount++
              }
            } catch (error) {
              errorCount++
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "rule" : "rules"
              } activated successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-admin_text_inv",
            })
          } else if (errorCount > 0) {
            showNotification({
              message: "Failed to activate rules",
              className: "bg-admin_error text-white",
            })
          }

          // Reset selection and refresh
          setSelectedItems([])
          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          showNotification({
            message: "An error occurred while activating rules",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Handle deactivating multiple rules
  const handleDeactivateSelected = () => {
    if (selectedItems.length === 0 || processing) return

    showPromptDialog({
      variant: "admin",
      title: "Confirm Deactivate",
      message: `Are you sure you want to deactivate ${selectedItems.length} ${
        selectedItems.length === 1 ? "rule" : "rules"
      }?`,
      confirmText: "Deactivate",
      confirmVariant: "error_m",
      onConfirm: async () => {
        setProcessing(true)
        try {
          let successCount = 0
          let errorCount = 0

          // Process each rule sequentially
          for (const ruleId of selectedItems) {
            try {
              const response = await UpdateSpamRule(ruleId, {
                active: false,
              })

              if (response.success) {
                successCount++
              } else {
                errorCount++
              }
            } catch (error) {
              errorCount++
            }
          }

          if (successCount > 0) {
            showNotification({
              message: `${successCount} ${
                successCount === 1 ? "rule" : "rules"
              } deactivated successfully${
                errorCount > 0 ? `, ${errorCount} failed` : ""
              }`,
              className: "bg-admin_primary text-admin_text_inv",
            })
          } else if (errorCount > 0) {
            showNotification({
              message: "Failed to deactivate rules",
              className: "bg-admin_error text-white",
            })
          }

          // Reset selection and refresh
          setSelectedItems([])
          setRefreshKey((prev) => prev + 1)
        } catch (error) {
          showNotification({
            message: "An error occurred while deactivating rules",
            className: "bg-admin_error text-white",
          })
        } finally {
          setProcessing(false)
        }
      },
    })
  }

  // Build match parameters for API
  const getRequestFields = () => {
    const match = {}

    // Handle active filter
    const selectedActive = activeFilter[0]?.value
    if (selectedActive !== null) {
      match.active = selectedActive
    }

    // Handle target filter
    const selectedTarget = targetFilter[0]?.value
    if (selectedTarget !== null) {
      // If we're looking for a specific target
      match.targets = selectedTarget
    }

    // Handle score filter
    const selectedScore = scoreFilter[0]?.value
    if (selectedScore !== null) {
      switch (selectedScore) {
        case "high":
          match.score = { $gte: 0.7 }
          break
        case "medium":
          match.score = { $gte: 0.4, $lt: 0.7 }
          break
        case "low":
          match.score = { $lt: 0.4 }
          break
        default:
          break
      }
    }

    return { match }
  }

  // Style overrides for the DataTable
  const styleOverrides = {
    table: "w-full table-fixed",
    searchInput: "absolute top-6 right-4 text-text bg-transparent outline-none flex-1 w-[300px] xl:w-[350px] h-[30px] px-2 py-1",
    row: "h-[60px] text-admin_text text-sm xl:text-base font-normal",
    getRowStyle: (index, item) => {
      // Check if this item is selected
      if (item && selectedItems.includes(item._id)) {
        return "bg-admin_canvas" // Selected row style
      }
    },
  }

  // Column definitions with selection checkbox
  const columns = [
    {
      id: "select",
      headerClassName: "w-[5%]",
      header: (value, items) => (
        <Checkbox
          variant="admin"
          isOn={items.length > 0 && selectedItems.length === items.length}
          onChange={(isSelected) => handleSelectAll(items, isSelected)}
        />
      ),
      render: (value, item) => (
        <Checkbox
          variant="admin"
          isOn={selectedItems.includes(item._id)}
          onChange={(isSelected) => handleSelectItem(item._id, isSelected)}
        />
      ),
    },
    {
      id: "active",
      title: "Status",
      headerClassName: "w-[16%]",
      render: (_, item) => (
        <div className="flex items-center flex-shrink-0">
          <ToggleSwitch
            variant="admin"
            isOn={item.active}
            onChange={(newStatus) => handleToggleRuleStatus(item, newStatus)}
          />

          <div className="ml-2">
            <Typography
              variant="admin_txt2"
              className={twMerge(
                "text-white font-medium rounded-sm  px-2 py-1",
                item.active
                  ? "bg-admin_success"
                  : "bg-admin_error"
              )}
            >
              {item.active ? "Active" : "Inactive"}
            </Typography>
          </div>
        </div>
      ),
    },
    {
      id: "description",
      title: "Description",
      headerClassName: "w-[18%]",
      render: (_, item) => (
        <div className="truncate max-w-full">
          <span>
            {item.description || "No description"}
          </span>
        </div>
      ),
    },
    {
      id: "pattern",
      title: "Pattern",
      headerClassName: "w-[18%]",
      render: (_, item) => (
        <div className="flex items-center">
          <span className="whitespace-nowrap truncate max-w-[250px]">
            {item.pattern}
          </span>
        </div>
      ),
    },
    {
      id: "score",
      title: "Score",
      headerClassName: "w-[12%]",
      render: (_, item) => {
        // Get the score value, defaulting to 0.5 if undefined
        const scoreValue = item.score !== undefined ? item.score : 0.5
        // Get the color class based on score
        const colorClass = getScoreColor(scoreValue)

        return (
          <div className="flex items-center gap-1">
            <span className={`font-medium ${colorClass} min-w-[30px]`}>
              {scoreValue.toFixed(1)}
            </span>

            <div className="w-full">
              <ProgressBar
                progress={scoreValue}
                title=""
                styleOverrides={{
                  container: "min-h-0",
                  titleContainer: "hidden",
                  progressContainer: "h-2 rounded-full bg-admin_neutral_1",
                  progressFill: `${colorClass.replace("text-", "bg-")}`,
                }}
              />
            </div>
          </div>
        )
      },
    },
    {
      id: "targets",
      title: "Targets",
      headerClassName: "w-[12%] text-center",
      render: (_, item) => (
        <div className="flex flex-wrap gap-1 max-w-full justify-center">
          {item.targets && item.targets.length > 0 ? (
            <>
              {item.targets.map((target, index) => (
                <span
                  key={index}
                  className="bg-admin_info_bg rounded-full text-xs whitespace-nowrap px-2 py-0.5"
                >
                  {target}
                </span>
              ))}
            </>
          ) : (
            <span className="text-admin_text_secondary">
              No targets
            </span>
          )}
        </div>
      ),
    },
    {
      id: "hitCount",
      title: "Hit Count",
      headerClassName: "w-[10%] whitespace-nowrap text-center",
      render: (_, item) => (
        <span className="block text-center">
          {item.hitCount || 0}
        </span>
      ),
    },
    {
      id: "actions",
      title: "Actions",
      headerClassName: "w-[12%] text-center",
      render: (_, item) => (
        <div className="flex justify-center gap-2 mx-auto">
          <Button
            variant="admin_icon"
            className="text-admin_text_secondary transition-colors px-3"
            onClick={() => handleEditRule(item)}
            title="Edit rule"
          >
            <PencilIcon className="w-5 h-5" />
          </Button>

          <Button
            className="text-admin_text_secondary hover:text-admin_error transition-colors px-3"
            onClick={() => handleDeleteRule(item)}
            title="Delete rule"
          >
            <TrashIcon className="w-5 h-5" />
          </Button>
        </div>
      ),
    },
  ]

  // Header controls
  const headerControls = (
    <div className="w-full flex flex-row items-center gap-3 -mt-7">
      {/* Active filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Status
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All Rules" },
            { value: true, label: "Active Rules" },
            { value: false, label: "Inactive Rules" },
          ]}
          value={activeFilter}
          onChange={(values) => {
            if (values && values.length > 0) {
              setActiveFilter([values[0]])
            } else {
              setActiveFilter([{ value: null, label: "All Rules" }])
            }
          }}
          className="w-[180px]"
          placeholder="Filter by status"
        />
      </div>

      {/* Target filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Target
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All Targets" },
            { value: "email", label: "Email" },
            { value: "subject", label: "Subject" },
            { value: "content", label: "Content" },
            { value: "all", label: "All Components" },
          ]}
          value={targetFilter}
          onChange={(values) => {
            if (values && values.length > 0) {
              setTargetFilter([values[0]])
            } else {
              setTargetFilter([{ value: null, label: "All Targets" }])
            }
          }}
          className="w-[180px]"
          placeholder="Filter by target"
        />
      </div>

      {/* Score filter */}
      <div className="flex flex-col">
        <Typography 
          variant="admin_txt2_dim"
          className="mb-0.5"
        >
          Score
        </Typography>

        <DropDownSelect
          variant="admin"
          options={[
            { value: null, label: "All Scores" },
            { value: "high", label: "High (≥0.7)" },
            { value: "medium", label: "Medium (0.4-0.7)" },
            { value: "low", label: "Low (<0.4)" },
          ]}
          value={scoreFilter}
          onChange={(values) => {
            if (values && values.length > 0) {
              setScoreFilter([values[0]])
            } else {
              setScoreFilter([{ value: null, label: "All Scores" }])
            }
          }}
          className="w-[180px]"
          placeholder="Filter by score"
        />
      </div>
    </div>
  )

  // Table footer content - action buttons
    const tableFooter = selectedItems.length > 0 && (
      <div className="flex justify-end items-center mt-4 p-3">
        <Typography variant="admin_body1" className="mr-4">
          {selectedItems.length}{" "}
          {selectedItems.length === 1 ? "rule" : "rules"} selected
        </Typography>

        <Button
          variant="admin_secondary"
          className="mr-3"
          onClick={handleClearSelection}
          disabled={processing}
        >
          <div className="flex items-center">
            Deselect
          </div>
        </Button>

        <Button
          variant="admin_secondary"
          className="mr-3"
          onClick={handleActivateSelected}
          disabled={processing}
        >
          <div className="flex items-center">
            <CheckIcon className="w-5 h-5 mr-1" />
            Activate
          </div>
        </Button>

        <Button
          variant="admin_error"
          onClick={handleDeactivateSelected}
          disabled={processing}
        >
          <div className="flex items-center">
            <XMarkIcon className="w-5 h-5 mr-2" />
            Deactivate
          </div>
        </Button>
      </div>
    )

  return (
    <div className="p-4">
      <div className="w-full flex items-center justify-between mb-5">
        <Typography 
          component="h1"
          variant="admin_h3"
          className="ml-4"
        >
          Spam Filter Rules
        </Typography>

        <Button
          variant="admin_primary"
          className="whitespace-nowrap flex items-center"
          onClick={() => setAddRuleDialog({ isOpen: true, rule: null })}
        >
          <PlusCircleIcon className="w-5 h-5 mr-2" /> Add
        </Button>
      </div>
      
      {/* Main data table */}
      <DataTable
        variant="admin_browser"
        apiMethod={GetSpamRules}
        requestFields={getRequestFields()}
        headerControls={headerControls}
        tableFooter={tableFooter}
        searchField="pattern description"
        sort={{ createdAt: -1 }}
        localUserPreferencesKey="admin.spamProtection.rules"
        renders={columns}
        refreshKey={refreshKey}
        preferredHeight="60vh"
        itemHeight="h-16"
        noData={<Typography variant="admin_body1">No rules found</Typography>}
        styleOverrides={styleOverrides}
      />

      {/* Add/Edit Rule Dialog */}
      <AddRuleDialog
        isOpen={addRuleDialog.isOpen}
        rule={addRuleDialog.rule}
        onClose={() => setAddRuleDialog({ isOpen: false, rule: null })}
        onRuleAdded={() => {
          setRefreshKey((prev) => prev + 1)
        }}
      />
    </div>
  )
}
